import { useContext, useEffect } from "react";
import { ApolloError } from "@apollo/client";
import { UiSchema } from "@rjsf/utils";
import { get } from "lodash";
import { useLocation } from "react-router";
import { SUBMISSION_CATEGORY } from "common/constants";
import { buildLink } from "common/routing";
import {
  getDocumentFieldsFromUISchema,
  hasRoleOrGroupPermission,
} from "common/services/submissionHelpers";
import { formatCoordinates } from "common/utils/coordinates";
import { SUBMISSION_CATEGORY_TO_LABEL } from "common/utils/submissions";
import {
  DefaultedValueArgs,
  DefaultedValuesArgs,
  useCommonDefaultedValue,
  useCommonDefaultedValues,
} from "common-client/utils/submissions";
import { AccountDocumentType, SubmissionType } from "../../generated/graphql";
import { AuthContext } from "../Authorization/AuthContext";
import {
  CurrentAdmin,
  CurrentPublicUser,
  CurrentUser,
} from "../Authorization/types";
import { SubmissionFormContext } from "./Form";

export const useDefaultedValues = <
  Keys extends string = string,
  Values extends Record<Keys, any> = Record<Keys, any>,
  Schema extends Record<keyof Values, UiSchema | undefined> = Record<
    keyof Values,
    UiSchema | undefined
  >,
>(
  args: DefaultedValuesArgs<Keys, Values, Schema>,
) => {
  const { editing, defaultValues } = useContext(SubmissionFormContext);
  return useCommonDefaultedValues({
    ...args,
    useEffect,
    defaultValues,
    editing,
  });
};

export const useDefaultedValue = <Value,>(args: DefaultedValueArgs<Value>) => {
  const { editing, defaultValues } = useContext(SubmissionFormContext);
  return useCommonDefaultedValue({
    ...args,
    useEffect,
    defaultValues,
    editing,
  });
};

export const generateUpsertErrorMessage = (error: ApolloError) => {
  const message =
    (
      error.message.includes("Invalid PDF file") ||
      error.message.includes("A record with the unique ID already exists.") ||
      error.message.includes(
        "Please contact support@withforerunner.com for assistance.",
      )
    ) ?
      error.message
    : "Your submission failed to process. Please try again.";

  return message;
};

export const castEmptyStringToNull = ({
  value,
  required,
}: {
  value: Maybe<string>;
  required: boolean;
}) => {
  // see commit message for why this `if` is necessary
  if (required) {
    return value === "" ? null : value;
  } else {
    return value;
  }
};

export const hasSummary = ({ summaries }: { summaries: Array<unknown> }) => {
  return summaries.length > 0;
};

type GetObjectDisplayProps = {
  property?: {
    fullAddress?: string | null;
    longitude: number;
    latitude: number;
  } | null;
  customMapGeometry?: {
    label: string;
  } | null;
};

export const getObjectDisplay = ({
  property,
  customMapGeometry,
}: GetObjectDisplayProps) => {
  if (property) {
    return property.fullAddress ?? formatCoordinates({ ...property });
  } else if (customMapGeometry) {
    return customMapGeometry.label;
  }
  return undefined;
};

export const useSubmissionPrevLocation = () => {
  const { isPublic } = useContext(AuthContext);
  const location =
    useLocation<Maybe<{ prevLocation?: string; search?: string }>>();
  let prevLocation: string;
  if (location.state?.prevLocation) {
    prevLocation = location.state.prevLocation;
  } else if (isPublic) {
    prevLocation = buildLink("properties");
  } else {
    prevLocation = `${buildLink("map")}${location.state?.search ?? ""}`;
  }

  return prevLocation;
};

export const SUBMISSION_CATEGORY_OPTIONS: Array<{
  value: SUBMISSION_CATEGORY;
  label: string;
}> = Object.entries(SUBMISSION_CATEGORY_TO_LABEL).map(([value, label]) => ({
  value: value as SUBMISSION_CATEGORY,
  label,
}));

export const submissionTypeContainsEcDocumentType = <
  T extends {
    currentVersion: {
      formStructure: {
        schema: any;
        uiSchema: any;
      };
    };
  },
>({
  submissionType,
  accountDocumentTypes = [],
}: {
  submissionType: T;
  accountDocumentTypes?: Pick<AccountDocumentType, "id" | "documentType">[];
}): boolean => {
  const { schema, uiSchema } = submissionType.currentVersion.formStructure;
  const fields = getDocumentFieldsFromUISchema({ schema, uiSchema });

  return fields.some(field => {
    const accountDocumentTypeId = get(
      schema,
      `${field.schemaPath}.items.properties.accountDocumentTypeId.enum`,
    )?.[0];

    return accountDocumentTypes.some(
      adt =>
        adt.id === accountDocumentTypeId &&
        adt.documentType?.name === "Elevation Certificate",
    );
  });
};

export const canCreateSubmissionTypeFromAuthInfo = ({
  submissionType,
  user,
  admin,
  publicUser,
}: {
  submissionType: Pick<SubmissionType, "canCreate"> & {
    canCreateGroups: Array<{ id: string }>;
  };
  user: Maybe<CurrentUser>;
  admin: Maybe<CurrentAdmin>;
  publicUser: Maybe<CurrentPublicUser>;
}) => {
  const role =
    user?.role.name ?? admin?.role.name ?? publicUser?.role.name ?? "guest";

  const userGroups = (publicUser && (publicUser?.userGroups ?? [])) ?? [];

  return hasRoleOrGroupPermission({
    allowedRolesAndGroupIds: [
      ...submissionType.canCreate,
      ...submissionType.canCreateGroups.map(userGroup => userGroup.id),
    ],
    role,
    userGroupIds: userGroups.map(userGroup => userGroup.id),
  });
};
